<template>
  <ModuleWrapper
    :moduleId="moduleId"
    :image="imageSrcPath('assets/branding/call-to-action.svg')"
    title="Call To Action Button"
    @initializeData="initializeData"
  >
    <template #main>
      <div class="tw-mb-2 tw-block tw-text-xs">
        Customize a call to action button and message text for your attendees. Use to prompt attendees to take action
        like request a demo, claim swag, or ask for a follow up from your team.
      </div>
      <StudioToggleWithLabel
        id="callToAction-enable-toggle"
        class="tw-mt-4"
        :label="`Button ${isCTAButtonEnabled ? 'Enabled' : 'Disabled'}`"
        :checked="isCTAButtonEnabled"
        @update:checked="toggleCTAButton"
      />
      <template v-if="isCTAButtonEnabled">
        <StudioInput
          id="cta-button-text-input"
          label="Button Text"
          class="tw-mb-4 tw-mt-7"
          :modelValue="ctaButtonText"
          :maxlength="27"
          :inlineError="buttonTextInvalidError"
          @update:modelValue="value => editCTAButtonText(value)"
        />
        <StudioSelect
          id="cta-demo-link-select"
          label="Link to"
          :options="[
            { value: 'confirmationMessage', label: 'Display confirmation message' },
            { value: 'linkToNewTab', label: 'Open link in new tab' }
          ]"
          size="small"
          orientation="horizontal"
          class="tw-mb-4"
          :value="selectedCTAButtonLinkTo"
          @change="handleLinkToChange"
        />
        <StudioInput
          v-if="selectedCTAButtonLinkTo === 'linkToNewTab'"
          id="cta-demo-button-url"
          label="URL"
          class="tw-mb-7"
          :modelValue="ctaDemoButtonUrl"
          placeholder="https://..."
          :inlineError="ctaButtonUrlInvalidError"
          @update:modelValue="value => editDemoButtonUrl(value)"
        />
        <template v-if="selectedCTAButtonLinkTo === 'confirmationMessage'">
          <StudioSelect
            v-if="useTwoStepCTA"
            id="cta-submit-type-select"
            label="Submit Type"
            class="tw-mb-4 tw-mt-4"
            :options="[
              { value: 'ONE_CLICK', label: 'One Click' },
              { value: 'TWO_STEP', label: 'Two Step' }
            ]"
            :value="ctaSubmitType"
            @change="onSelectSubmitType"
          />
          <template v-if="useTwoStepCTA && ctaSubmitType === 'TWO_STEP'">
            <StudioInput
              id="cta-consent-title-input"
              label="Confirm Title"
              class="tw-mb-4 tw-mt-4"
              :modelValue="ctaConsentTitle"
              :maxlength="120"
              :inlineError="consentTitleInvalidError"
              @update:modelValue="value => editCTAConsentTitle(value)"
            />
            <StudioTextarea
              id="cta-disclosure-text-input"
              label="Disclosure Text"
              class="tw-mb-7 tw-mt-4"
              :modelValue="ctaDisclosureText"
              :maxlength="500"
              :inlineError="disclosureTextInvalidError"
              @update:modelValue="value => editCTADisclosureText(value)"
            />
            <StudioInput
              id="cta-confirmation-title-input"
              label="Success Title"
              class="tw-mb-7 tw-mt-4"
              :modelValue="ctaConfirmationTitle"
              :maxlength="120"
              :inlineError="confirmationTitleInvalidError"
              @update:modelValue="value => editCTAConfirmationTitle(value)"
            />
            <StudioTextarea
              id="cta-confirmation-message-input"
              label="Success Message"
              class="tw-mb-7 tw-mt-4"
              :modelValue="ctaConfirmationMessage"
              :maxlength="350"
              :inlineError="confirmationMessageInvalidError"
              @update:modelValue="value => editCTAConfirmationMessage(value)"
            />
          </template>
          <template v-if="!useTwoStepCTA || ctaSubmitType === 'ONE_CLICK'">
            <StudioInput
              id="cta-confirmation-title-input"
              :label="`${useTwoStepCTA ? 'Confirmation' : 'Success'} Title`"
              class="tw-mb-7 tw-mt-4"
              :modelValue="ctaConfirmationTitle"
              :maxlength="120"
              :inlineError="confirmationTitleInvalidError"
              @update:modelValue="value => editCTAConfirmationTitle(value)"
            />
            <StudioTextarea
              id="cta-confirmation-message-input"
              :label="`${useTwoStepCTA ? 'Confirmation' : 'Success'} Message`"
              class="tw-mb-7 tw-mt-4"
              :modelValue="ctaConfirmationMessage"
              :maxlength="350"
              :inlineError="confirmationMessageInvalidError"
              @update:modelValue="value => editCTAConfirmationMessage(value)"
            />
          </template>
        </template>
        <StudioToggleWithLabel
          v-if="isConference"
          id="callToAction-ondemand-enable-toggle"
          class="tw-mt-4"
          :label="`On-Demand Button ${isOnDemandCTAButtonEnabled ? 'Enabled' : 'Disabled'}`"
          :checked="isOnDemandCTAButtonEnabled"
          @update:checked="toggleOnDemandCTAButton"
        />
      </template>
    </template>
  </ModuleWrapper>
</template>

<script>
import debounce from 'lodash.debounce';
import { mapMutations, mapGetters } from 'vuex';

import ModuleWrapper from './ModuleWrapper.vue';
import { DemoRequestSubmitType } from '../types/theme.types';

import { StudioInput, StudioTextarea, StudioToggleWithLabel, StudioSelect } from '@/studioComponents';
import { isValidUrl } from '@/utils/url';

function getInputValidationError(input) {
  if (!input?.trim().length) {
    return 'Required';
  }
  return '';
}

export default {
  name: 'CallToActionButton',
  components: {
    ModuleWrapper,
    StudioToggleWithLabel,
    StudioInput,
    StudioTextarea,
    StudioSelect
  },
  props: {
    moduleId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isCTAButtonEnabled: false,
      isOnDemandCTAButtonEnabled: false,
      ctaButtonText: '',
      ctaSubmitType: DemoRequestSubmitType.ONE_CLICK,
      ctaConsentTitle: '',
      ctaDisclosureText: '',
      ctaConfirmationTitle: '',
      ctaConfirmationMessage: '',
      ctaDemoButtonUrl: '',
      selectedCTAButtonLinkTo: 'confirmationMessage'
    };
  },
  mounted() {
    this.initializeData();
  },
  computed: {
    ...mapGetters('branding', {
      getInitialModuleData: 'getInitialModuleData'
    }),
    ...mapGetters('studioEventList', {
      currentEvent: 'getCurrentEvent'
    }),
    ...mapGetters('org', {
      currentOrg: 'getCurrentOrg'
    }),
    useTwoStepCTA() {
      return this.currentOrg?.useTwoStepCTA || false;
    },
    buttonTextInvalidError() {
      return getInputValidationError(this.ctaButtonText);
    },
    confirmationTitleInvalidError() {
      return getInputValidationError(this.ctaConfirmationTitle);
    },
    confirmationMessageInvalidError() {
      return getInputValidationError(this.ctaConfirmationMessage);
    },
    consentTitleInvalidError() {
      if (this.useTwoStepCTA && this.ctaSubmitType === DemoRequestSubmitType.TWO_STEP) {
        return getInputValidationError(this.ctaConsentTitle);
      }
      return '';
    },
    disclosureTextInvalidError() {
      if (this.useTwoStepCTA && this.ctaSubmitType === DemoRequestSubmitType.TWO_STEP) {
        return getInputValidationError(this.ctaDisclosureText);
      }
      return '';
    },
    ctaButtonUrlInvalidError() {
      if (this.selectedCTAButtonLinkTo !== 'linkToNewTab') {
        return '';
      }

      const requiredError = getInputValidationError(this.ctaDemoButtonUrl);
      if (requiredError) {
        return requiredError;
      }

      // Check if URL is valid
      if (!isValidUrl(this.ctaDemoButtonUrl.trim())) {
        return 'Please enter a valid URL';
      }

      return ''; // Return empty string when URL is valid
    },
    isFormValid() {
      const baseValidation =
        !this.buttonTextInvalidError &&
        !this.confirmationTitleInvalidError &&
        !this.confirmationMessageInvalidError &&
        !this.ctaButtonUrlInvalidError;

      if (this.useTwoStepCTA && this.ctaSubmitType === DemoRequestSubmitType.TWO_STEP) {
        return baseValidation && !this.consentTitleInvalidError && !this.disclosureTextInvalidError;
      }

      return baseValidation;
    },
    isConference() {
      return this.currentEvent?.event_type === 'Conference';
    }
  },
  watch: {
    isFormValid(newInputs) {
      if (this.isCTAButtonEnabled) {
        this.setCurrentModuleInvalid(!newInputs);
      } else {
        this.setCurrentModuleInvalid(false);
      }
    },
    isCTAButtonEnabled(CTAEnabled) {
      if (!CTAEnabled) {
        if (!this.ctaButtonText) {
          this.ctaButtonText = 'Get Demo';
          this.setChangesMadeInEvent({
            demo_request_button_text: this.ctaButtonText
          });
        }
        if (!this.ctaConfirmationTitle) {
          this.ctaConfirmationTitle = 'Demo request sent!';
          this.setChangesMadeInEvent({
            demo_request_title: this.ctaConfirmationTitle
          });
        }
        if (!this.ctaConfirmationMessage) {
          this.ctaConfirmationMessage = 'Your request was successfully saved. Thanks for requesting a demo with us';
          this.setChangesMadeInEvent({
            demo_request_message: this.ctaConfirmationMessage
          });
        }
        this.setCurrentModuleInvalid(false);
      }
    }
  },
  methods: {
    ...mapMutations('branding', {
      setChangesMadeInEvent: 'setChangesMadeInEvent',
      setCurrentModuleInvalid: 'setCurrentModuleInvalid',
      setChangesMadeInThemeObj: 'setChangesMadeInThemeObj'
    }),
    initializeData() {
      const moduleKeys = [
        'demo_link_enabled',
        'on_demand_demo_link_enabled',
        'demo_request_button_text',
        'demo_request_message',
        'demo_request_title',
        'demo_request_submit_type',
        'demo_request_consent_title',
        'demo_request_disclosure_text',
        'custom_demo_cta_link_to',
        'custom_demo_cta_button_url'
      ];
      const {
        demo_link_enabled,
        demo_request_button_text,
        demo_request_title,
        demo_request_message,
        demo_request_submit_type,
        demo_request_consent_title,
        demo_request_disclosure_text,
        on_demand_demo_link_enabled,
        custom_demo_cta_link_to,
        custom_demo_cta_button_url
      } = this.getInitialModuleData(moduleKeys);
      this.isCTAButtonEnabled = demo_link_enabled;
      this.isOnDemandCTAButtonEnabled = on_demand_demo_link_enabled;
      this.ctaButtonText = demo_request_button_text;
      this.ctaSubmitType = demo_request_submit_type || DemoRequestSubmitType.ONE_CLICK;
      this.ctaConsentTitle = demo_request_consent_title || 'Confirm request';
      this.ctaDisclosureText =
        demo_request_disclosure_text ||
        'By clicking confirm, you agree to be contacted by our team regarding your demo request. We will use the information you provide to reach out to you.';
      this.ctaConfirmationTitle = demo_request_title;
      this.ctaConfirmationMessage = demo_request_message;
      this.selectedCTAButtonLinkTo = custom_demo_cta_link_to || 'confirmationMessage';
      this.ctaDemoButtonUrl = custom_demo_cta_button_url;
    },
    toggleCTAButton(event) {
      const checked = event.target.checked;

      this.isCTAButtonEnabled = checked;
      this.setChangesMadeInEvent({
        demo_link_enabled: checked || false
      });
    },
    toggleOnDemandCTAButton(event) {
      const checked = event.target.checked;

      this.isOnDemandCTAButtonEnabled = checked;
      this.setChangesMadeInEvent({
        on_demand_demo_link_enabled: checked || false
      });
    },
    editCTAButtonText: debounce(function (value) {
      this.ctaButtonText = value;
      this.setChangesMadeInEvent({
        demo_request_button_text: this.ctaButtonText
      });
    }, 500),
    editCTAConfirmationTitle: debounce(function (value) {
      this.ctaConfirmationTitle = value;
      this.setChangesMadeInEvent({
        demo_request_title: this.ctaConfirmationTitle
      });
    }, 500),
    editCTAConfirmationMessage: debounce(function (value) {
      this.ctaConfirmationMessage = value;
      this.setChangesMadeInEvent({
        demo_request_message: this.ctaConfirmationMessage
      });
    }, 500),
    onSelectSubmitType(e) {
      this.ctaSubmitType = e.target.value;
      if (this.ctaSubmitType === 'TWO_STEP') {
        this.setChangesMadeInEvent({
          demo_request_consent_title: this.ctaConsentTitle,
          demo_request_disclosure_text: this.ctaDisclosureText
        });
      }
      this.setChangesMadeInEvent({
        demo_request_submit_type: this.ctaSubmitType
      });
    },
    editCTAConsentTitle: debounce(function (value) {
      this.ctaConsentTitle = value;
      this.setChangesMadeInEvent({
        demo_request_consent_title: this.ctaConsentTitle
      });
    }, 500),
    editCTADisclosureText: debounce(function (value) {
      this.ctaDisclosureText = value;
      this.setChangesMadeInEvent({
        demo_request_disclosure_text: this.ctaDisclosureText
      });
    }, 500),
    handleLinkToChange(e) {
      this.selectedCTAButtonLinkTo = e.target.value;
      this.setChangesMadeInThemeObj({
        custom_demo_cta_link_to: this.selectedCTAButtonLinkTo
      });
    },
    editDemoButtonUrl(value) {
      this.ctaDemoButtonUrl = value;
      this.setChangesMadeInThemeObj({
        custom_demo_cta_button_url: value
      });
    }
  }
};
</script>
